import dayjs from "dayjs";
import { parseImmatriculation } from "../../../../../variables/functions";

export const getEmptyConducteurForm = () => ({
    personneId: 0,
    codeId: "",
    nom: "",
    prenom: "",
    genre: "MASCULIN",
    profession: "",
    email: "",
    adresse: "",
    telephone: "",
    contact: [],
    pieces: [],
    typePiece: null,
    numIdentite: "",
    lieuDelivrance: "",
    dateDelivrance: "",
    dateNaissance: "",
    typePersonne: "PHYSIQUE",
});

export const getEmptyEnginForm = () => ({
    categorie: "MOTO",
    marque: "",
    numeroSerie: "",
    matricule: "",
    typeSerie: "TG",
    immatriculation: "",
    immatriculationFacultative: false,
    puissance: 1,
    volume: "",
    nombrePlace: 0,
    energie: "ESSENCE",
    numeroChassis: "",
    valeurDeclaree: "",
    valeurVenale: "",
    valeurANeuf: "",
    dateMiseEnCirculation: "",
    puissanceType: [],
    sousGaranties: [],
    garanties: [],
    usage: "TAXI",
    carosserie: null,
    moteur: "",
    type: "",
    genre: null,
    concessionnaire: null,
    rc: 0,
    carteGrise: "",
    personne: getEmptyConducteurForm(),
    useSouscripteur: true,
    codeId: "",
});

export const mapApiPersonToConducteurForm = (person = {}) => ({
    personneId: person.personneId || 0,
    codeId: person.codeId || "",
    nom: person.nom || "",
    prenom: person.prenom || "",
    genre: person.genre || "MASCULIN",
    profession: person.profession || "",
    email: person.email || "",
    adresse: person.adresse || "",
    telephone: Array.isArray(person.contact)
        ? (person.contact[0] || "").replace(/\s/g, "")
        : (person.telephone || "").replace(/\s/g, ""),
    contact: Array.isArray(person.contact)
        ? person.contact.filter(Boolean).map((c) => String(c).replace(/\s/g, ""))
        : [],
    pieces: Array.isArray(person.pieces) ? person.pieces : [],
    typePiece: person.typePiece || null,
    numIdentite: person.numIdentite || "",
    lieuDelivrance: person.lieuDelivrance || "",
    dateDelivrance: person.dateDelivrance || "",
    dateNaissance: person.dateNaissance || "",
});

export const mapEnginToUpdateForm = (engin, assured, index) => {
    const { typeSerie, numeroSerie, matricule } = parseImmatriculation(engin.immatriculation);

    // Pour une MOTO, on RECALCULE l'immatriculation au bon format (numéro série + type série
    // + matricule) à partir des parties extraites. Corrige ainsi, au prochain enregistrement,
    // les propositions sauvegardées avec l'ancien format erroné — sans manipulation manuelle.
    // (parseImmatriculation extrait par CONTENU : chiffres = matricule, lettres ≠ TG = n° série,
    //  donc fiable quel que soit l'ordre stocké.) Pour un véhicule, on garde l'immat telle quelle.
    const catCode = engin.categorie?.codeCategorie || "MOTO";
    const isMoto = String(catCode).toUpperCase().startsWith("M");
    // On ne recalcule QUE pour une moto réellement au format TG (numéro série ET matricule
    // présents) — sinon (moto non-TG = matricule seul, ou immat non parsable) on prependrait
    // « TG » à tort : on conserve alors l'immatriculation stockée.
    const canRecomputeMoto = isMoto && !!numeroSerie && !!matricule;
    const normalizedImmat = canRecomputeMoto
        ? (computeImmatriculation(catCode, "TG", numeroSerie, matricule) || engin.immatriculation || "")
        : (engin.immatriculation || "");

    return {
        categorie: engin.categorie?.codeCategorie || "MOTO",
        marque: engin.marque || "",
        numeroSerie: numeroSerie || "",
        matricule: matricule || "",
        typeSerie: typeSerie || "TG",
        immatriculation: normalizedImmat,
        immatriculationFacultative: false,
        oldImmatriculation: normalizedImmat,
        puissance: engin.puissance || 1,
        // L'input "Puissance (CV)" d'EnginForm est lié au champ `volume`.
        // Pour un VÉHICULE, `engin.volume` contient l'équivalent essence CONVERTI
        // (ex DIESEL : 9 CV → 13) servant à la tarification, PAS la puissance réelle.
        // L'afficher en édition ferait revenir "13" au lieu de "9" et, recopié dans
        // `puissance` à la saisie, provoquerait une double conversion (preview ≠ après save).
        // → on charge la VRAIE puissance d'origine ; le backend re-dérivera le volume.
        // Pour une MOTO, `volume` (cm³) est l'attribut réel : on le conserve.
        volume: `${engin.categorie?.codeCategorie || ""}`.toUpperCase().startsWith("M")
            ? (engin.volume || "")
            : (engin.puissance || engin.volume || ""),
        nombrePlace: engin.nombrePlace || 0,
        energie: engin.energie || "ESSENCE",
        numeroChassis: engin.numeroChassis || "",
        oldNumeroChassis: engin.numeroChassis || "",
        // `?? ""` (et non `|| ""`) : une valeur réellement à 0 doit s'afficher 0,
        // sinon (0 étant falsy) le champ paraîtrait vide et masquerait la donnée stockée.
        valeurDeclaree: engin.valeurDeclaree ?? "",
        valeurVenale: engin.valeurVenale ?? "",
        valeurANeuf: engin.valeurANeuf ?? "",
        dateMiseEnCirculation: engin.dateMiseEnCirculation
            ? dayjs(engin.dateMiseEnCirculation) // assuming dayjs for DatePicker
            : "",
        puissanceType: Array.isArray(engin.puissanceType) ? engin.puissanceType : [],
        sousGaranties: Array.isArray(engin.sousGaranties) ? engin.sousGaranties : [],
        garanties: Array.isArray(engin.garanties) ? engin.garanties : [],
        usage: engin.usage || "TAXI",
        carosserie: engin.carosserie || null,
        moteur: engin.moteur || "",
        type: engin.type || "",
        genre: engin.genre || null,
        concessionnaire: engin.concessionnaire || null,
        carteGrise: engin.carteGrise || "",
        personne: (() => {
            const source = engin.conducteurHabituel || assured || {};
            const formPerson = mapApiPersonToConducteurForm(source);
            formPerson.personneId = index;
            if (engin.conducteurHabituel) {
                formPerson.codeId = engin.conducteurHabituel.codeId || "";
            }
            if (!engin.conducteurHabituel) {
                // Conserver l'identifiant du souscripteur dans la personne pour référence
                formPerson.codeId = source.codeId || "";
            }
            return formPerson;
        })(),
        useSouscripteur: !engin.conducteurHabituel,
        codeId: engin.codeId || "",
    }
};

export const mapApiPersonToSouscripteurForm = (person = {}) => ({
    personneId: person.personneId || 0,
    codeId: person.codeId || "",
    nom: person.nom || "",
    prenom: person.prenom || "",
    genre: person.genre || "MASCULIN",
    typePersonne: person.typePersonne || "PHYSIQUE",
    profession: person.profession || "",
    email: person.email || "",
    adresse: person.adresse || "",
    telephone: Array.isArray(person.contact)
        ? (person.contact[0] || "").replace(/\s/g, "")
        : (person.telephone || "").replace(/\s/g, ""),
    contact: Array.isArray(person.contact)
        ? person.contact.filter(Boolean).map((c) => String(c).replace(/\s/g, ""))
        : [],
    pieces: Array.isArray(person.pieces) ? person.pieces : [],
    typePiece: person.typePiece || null,
    numIdentite: person.numIdentite || "",
    lieuDelivrance: person.lieuDelivrance || "",
    dateDelivrance: person.dateDelivrance || "",
    dateNaissance: person.dateNaissance || "",
    nif: person.nif || "",
    numeroRegistreCommerce: person.numeroRegistreCommerce || "",
});

export const computeImmatriculation = (categorie, typeSerie, numeroSerie, matricule) => {
    const catCode = (typeof categorie === "string" ? categorie : categorie?.codeCategorie) || "";
    const ts = (typeSerie || "").trim();
    const ns = (numeroSerie || "").trim();
    const m = (matricule || "").trim();

    const isVehicule = catCode.startsWith("V");
    const isMoto = catCode.startsWith("M");
    const isNonTGSerie = ts !== "TG";

    if (isVehicule || isNonTGSerie) {
        return m;
    }

    if (isMoto) {
        return `${ns}${ts}${m}`.trim();
    }

    return [ts, m, ns].filter(Boolean).join(" ").trim();
};

export const parseConcessionnaireValue = (value) => {
    if (!value) return null;
    if (typeof value === "string") {
        try {
            return JSON.parse(value);
        } catch (error) {
            console.warn("Impossible de parser le concessionnaire", error);
            return null;
        }
    }
    return value;
};

export const resolveCategorieInput = (value, selectedCategorie, categories) => {
    if (!value) {
        return selectedCategorie || null;
    }
    if (typeof value === "string") {
        return (
            categories.find(
                (cat) =>
                    cat.nom === value ||
                    cat.codeCategorie === value
            ) ||
            selectedCategorie ||
            value
        );
    }
    return value;
};

export const resolveCategorieForPayload = (value, selectedCategorie, categories) => resolveCategorieInput(value, selectedCategorie, categories);

export const normalizeConducteurPayload = (form = {}) => {
    if (!form) return null;
    const nom = (form.nom || "").trim();
    const prenom = (form.prenom || "").trim();
    const adresse = (form.adresse || "").trim();
    const telephone = (form.telephone || "").replace(/\s/g, "");
    if (!nom || !prenom || !adresse || !telephone) {
        return null;
    }

    const { contact = [], telephone: _rawTelephone, ...rest } = form;
    const contacts = telephone
        ? [telephone]
        : Array.isArray(contact)
            ? contact.filter(Boolean)
            : [];

    return {
        ...rest,
        contact: contacts,
    };
};

export const buildEnginPayload = (engin, selectedCategorie, categories) => {
    const { personne, immatriculationFacultative, ...rest } = engin;
    const conducteurPayload = normalizeConducteurPayload(personne);
    const hasConducteur = Boolean(conducteurPayload);
    return {
        ...rest,
        categorie: resolveCategorieForPayload(rest.categorie, selectedCategorie, categories),
        concessionnaire: parseConcessionnaireValue(rest.concessionnaire),
        conducteurHabituel: hasConducteur ? conducteurPayload : null,
        useSouscripteur: !hasConducteur,
    };
};
